<template>
  <div class="user-list-page">
    <!-- Toggle Usuarios / Solicitudes -->
    <div class="view-toggle mb-3">
      <button
        type="button"
        class="view-toggle__btn"
        :class="{ active: viewMode === 'users' }"
        @click="switchTo('users')"
      >
        <i class="fas fa-users me-2"></i>Usuarios
      </button>
      <button
        type="button"
        class="view-toggle__btn"
        :class="{ active: viewMode === 'requests' }"
        @click="switchTo('requests')"
      >
        <i class="fas fa-clipboard-list me-2"></i>Solicitudes de acceso
        <span v-if="requestsPendingCount" class="view-toggle__badge">{{ requestsPendingCount }}</span>
      </button>
    </div>

    <AccessRequestsManager v-if="viewMode === 'requests'" />

    <template v-else>
    <!-- Toolbar -->
    <div class="panel-toolbar">
      <div class="toolbar-left">
        <p class="panel-toolbar-meta mb-0">
          <span v-if="!loading">{{ pagination.count }} usuario{{ pagination.count !== 1 ? 's' : '' }}</span>
          <span v-else>Cargando usuarios…</span>
        </p>
        <div v-if="pendingCount > 0" class="pending-pill">
          <i class="fas fa-envelope me-1"></i>{{ pendingCount }} sin verificar email
        </div>
      </div>
      <div class="panel-toolbar-actions">
        <button class="btn btn-outline-light btn-sm" @click="refreshUsers" :disabled="loading">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
        </button>
        <button class="btn btn-primary" @click="openCreateUserModal">
          <i class="fas fa-user-plus me-2"></i>Nuevo usuario
        </button>
      </div>
    </div>

    <!-- Filtros rápidos + búsqueda -->
    <div class="filters-bar">
      <div class="search-wrap">
        <i class="fas fa-search search-icon"></i>
        <input
          v-model="filters.username"
          type="text"
          class="form-control search-input"
          placeholder="Buscar por nombre, usuario o email…"
          @input="debouncedSearch"
        />
        <button
          v-if="hasActiveFilters"
          type="button"
          class="clear-search"
          title="Limpiar filtros"
          @click="refreshUsers"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="panel-tabs-wrap w-100">
        <div class="quick-filters">
          <button
            v-for="chip in quickFilters"
            :key="chip.key"
            type="button"
            class="filter-chip"
            :class="{ active: activeQuickFilter === chip.key }"
            @click="applyQuickFilter(chip.key)"
          >
            <i :class="chip.icon" class="me-1"></i>{{ chip.label }}
          </button>
        </div>
      </div>

      <select v-model="filters.roleFilter" class="form-select role-select" @change="applyFilters">
        <option value="">Todos los roles</option>
        <option v-for="r in roleOptions" :key="r.slug" :value="r.slug">{{ r.label }}</option>
      </select>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="table-skeleton panel-table-wrap">
      <div v-for="n in 6" :key="n" class="skeleton-row">
        <div class="skeleton-avatar"></div>
        <div class="skeleton-lines">
          <div class="skeleton-line w-40"></div>
          <div class="skeleton-line w-60"></div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="state-box state-error">
      <i class="fas fa-exclamation-circle fa-2x mb-3"></i>
      <p>{{ error }}</p>
      <button class="btn btn-outline-light btn-sm mt-2" @click="searchUsers">
        <i class="fas fa-redo me-1"></i>Reintentar
      </button>
    </div>

    <!-- Tabla desktop / tarjetas móvil -->
    <template v-else-if="users.length">
      <div class="panel-mobile-cards d-md-none">
        <article v-for="user in users" :key="`m-${user.id}`" class="panel-data-card">
          <header class="panel-data-card__head">
            <div class="user-cell">
              <span class="avatar" :class="{ inactive: !user.is_active }">{{ userInitials(user) }}</span>
              <div class="user-cell-text">
                <span class="user-cell-name">{{ displayName(user) }}</span>
                <span class="user-cell-sub">@{{ user.username }}</span>
                <span class="user-cell-email">{{ user.email }}</span>
              </div>
            </div>
          </header>
          <div class="panel-data-card__rows">
            <div class="panel-data-card__row">
              <span class="panel-data-card__label">Rol</span>
              <span class="panel-data-card__value">
                <span v-if="user.is_staff" class="role-tag role-tag-admin">Admin</span>
                <select
                  v-else
                  class="role-inline-select"
                  :style="{ borderColor: user.role_color || '#adb5bd', color: user.role_color || '#adb5bd' }"
                  :value="user.role"
                  :disabled="changingRoleId === user.id"
                  @change="changeUserRole(user, $event.target.value)"
                >
                  <option v-for="r in roleOptions" :key="r.slug" :value="r.slug">{{ r.label }}</option>
                  <option v-if="!roleOptions.find(r => r.slug === user.role)" :value="user.role">{{ user.role_display || user.role }}</option>
                </select>
              </span>
            </div>
            <div class="panel-data-card__row">
              <span class="panel-data-card__label">Verificación</span>
              <span class="panel-data-card__value">
                <span class="status-badge" :class="user.email_verified ? 'status-ok' : 'status-warn'">
                  <i :class="user.email_verified ? 'fas fa-check-circle' : 'fas fa-envelope'" class="me-1"></i>
                  {{ user.email_verified ? 'Verificado' : 'Pendiente' }}
                </span>
              </span>
            </div>
            <div class="panel-data-card__row">
              <span class="panel-data-card__label">Actividad</span>
              <span class="panel-data-card__value">
                <span class="status-badge" :class="user.has_recent_purchase ? 'status-ok' : 'status-muted'">
                  <i :class="user.has_recent_purchase ? 'fas fa-shopping-bag' : 'fas fa-moon'" class="me-1"></i>
                  {{ user.has_recent_purchase ? 'Activo' : 'Inactivo' }}
                </span>
              </span>
            </div>
            <div v-if="!user.is_staff" class="panel-data-card__row">
              <span class="panel-data-card__label">B2B</span>
              <span class="panel-data-card__value">
                <span class="b2b-badge" :class="accountState(user).cls" :data-tooltip="accountState(user).tooltip">
                  <i :class="accountState(user).icon" class="me-1"></i>{{ accountState(user).text }}
                </span>
              </span>
            </div>
          </div>
          <footer class="panel-data-card__actions">
            <router-link :to="`/panel/usuarios/${user.id}`" class="btn btn-sm btn-icon" title="Ver detalle">
              <i class="fas fa-eye"></i>
            </router-link>
            <button
              v-if="!user.is_approved && !user.is_staff"
              type="button"
              class="btn btn-sm btn-icon btn-icon-success"
              title="Aprobar acceso B2B"
              @click="approveUser(user)"
            >
              <i class="fas fa-key"></i>
            </button>
            <button
              v-if="user.is_approved && !user.is_staff"
              type="button"
              class="btn btn-sm btn-icon btn-icon-warning"
              title="Revocar acceso B2B"
              @click="revokeUser(user)"
            >
              <i class="fas fa-key"></i>
            </button>
            <button
              v-if="!user.is_active && user.id !== currentUserId"
              type="button"
              class="btn btn-sm btn-icon btn-icon-success"
              title="Habilitar cuenta"
              @click="toggleUserStatus(user, true)"
            >
              <i class="fas fa-user-check"></i>
            </button>
            <button
              v-if="user.is_active && user.id !== currentUserId"
              type="button"
              class="btn btn-sm btn-icon btn-icon-warning"
              title="Deshabilitar cuenta"
              @click="toggleUserStatus(user, false)"
            >
              <i class="fas fa-user-slash"></i>
            </button>
            <button
              type="button"
              class="btn btn-sm btn-icon btn-icon-danger"
              title="Eliminar"
              :disabled="user.id === currentUserId"
              @click="confirmDeleteUser(user)"
            >
              <i class="fas fa-trash"></i>
            </button>
          </footer>
        </article>
      </div>

      <div class="table-responsive panel-table-wrap d-none d-md-block">
      <table class="table table-dark table-hover align-middle mb-0 user-table">
        <thead>
          <tr>
            <th>Usuario</th>
            <th class="d-none d-md-table-cell">Email</th>
            <th>Rol</th>
            <th>Verificación</th>
            <th>Actividad</th>
            <th class="d-none d-lg-table-cell">B2B</th>
            <th class="text-end">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in users" :key="user.id" class="user-row">
            <td>
              <div class="user-cell">
                <span class="avatar" :class="{ inactive: !user.is_active }">
                  {{ userInitials(user) }}
                </span>
                <div class="user-cell-text">
                  <span class="user-cell-name">
                    {{ displayName(user) }}
                  </span>
                  <span class="user-cell-sub">@{{ user.username }}</span>
                  <span class="user-cell-email d-md-none">{{ user.email }}</span>
                </div>
              </div>
            </td>
            <td class="d-none d-md-table-cell email-cell">{{ user.email }}</td>
            <td>
              <span v-if="user.is_staff" class="role-tag role-tag-admin">Admin</span>
              <div v-else class="role-select-wrap">
                <select
                  class="role-inline-select"
                  :style="{ borderColor: user.role_color || '#adb5bd', color: user.role_color || '#adb5bd' }"
                  :value="user.role"
                  :disabled="changingRoleId === user.id"
                  @change="changeUserRole(user, $event.target.value)"
                >
                  <option v-for="r in roleOptions" :key="r.slug" :value="r.slug">{{ r.label }}</option>
                  <option v-if="!roleOptions.find(r => r.slug === user.role)" :value="user.role">{{ user.role_display || user.role }}</option>
                </select>
                <i v-if="changingRoleId === user.id" class="fas fa-spinner fa-spin role-spin"></i>
              </div>
            </td>
            <td>
              <span
                class="status-badge"
                :class="user.email_verified ? 'status-ok' : 'status-warn'"
                :data-tooltip="user.email_verified
                  ? 'El usuario verificó su dirección de email'
                  : 'Pendiente: debe confirmar su email para activar la cuenta'"
              >
                <i :class="user.email_verified ? 'fas fa-check-circle' : 'fas fa-envelope'" class="me-1"></i>
                {{ user.email_verified ? 'Email verificado' : 'Email pendiente' }}
              </span>
            </td>
            <td>
              <span
                class="status-badge"
                :class="user.has_recent_purchase ? 'status-ok' : 'status-muted'"
                :data-tooltip="user.has_recent_purchase
                  ? 'Realizó al menos una compra en los últimos 2 meses'
                  : 'Sin compras en los últimos 2 meses'"
              >
                <i :class="user.has_recent_purchase ? 'fas fa-shopping-bag' : 'fas fa-moon'" class="me-1"></i>
                {{ user.has_recent_purchase ? 'Activo' : 'Inactivo' }}
              </span>
            </td>
            <td class="d-none d-lg-table-cell">
              <span v-if="user.is_staff" class="text-muted">—</span>
              <span
                v-else
                class="b2b-badge"
                :class="accountState(user).cls"
                :data-tooltip="accountState(user).tooltip"
              >
                <i :class="accountState(user).icon" class="me-1"></i>{{ accountState(user).text }}
              </span>
            </td>
            <td class="text-end">
              <div class="row-actions">
                <router-link
                  :to="`/panel/usuarios/${user.id}`"
                  class="btn btn-sm btn-icon"
                  title="Ver detalle"
                >
                  <i class="fas fa-eye"></i>
                </router-link>
                <button
                  v-if="!user.is_approved && !user.is_staff"
                  type="button"
                  class="btn btn-sm btn-icon btn-icon-success"
                  title="Aprobar acceso B2B"
                  @click="approveUser(user)"
                >
                  <i class="fas fa-key"></i>
                </button>
                <button
                  v-if="user.is_approved && !user.is_staff"
                  type="button"
                  class="btn btn-sm btn-icon btn-icon-warning"
                  title="Revocar acceso B2B"
                  @click="revokeUser(user)"
                >
                  <i class="fas fa-key"></i>
                </button>
                <button
                  v-if="!user.is_active && user.id !== currentUserId"
                  type="button"
                  class="btn btn-sm btn-icon btn-icon-success"
                  title="Habilitar cuenta para iniciar sesión"
                  @click="toggleUserStatus(user, true)"
                >
                  <i class="fas fa-user-check"></i>
                </button>
                <button
                  v-if="user.is_active && user.id !== currentUserId"
                  type="button"
                  class="btn btn-sm btn-icon btn-icon-warning"
                  title="Deshabilitar cuenta (no podrá iniciar sesión)"
                  @click="toggleUserStatus(user, false)"
                >
                  <i class="fas fa-user-slash"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-icon btn-icon-danger"
                  title="Eliminar"
                  :disabled="user.id === currentUserId"
                  @click="confirmDeleteUser(user)"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
    </template>

    <!-- Vacío -->
    <div v-else class="state-box">
      <i class="fas fa-users fa-3x mb-3 text-muted"></i>
      <h3>No se encontraron usuarios</h3>
      <p class="text-muted">Probá con otros filtros o creá un usuario nuevo.</p>
      <button v-if="hasActiveFilters" class="btn btn-outline-light btn-sm mt-2" @click="refreshUsers">
        Limpiar filtros
      </button>
    </div>

    <!-- Paginación -->
    <div v-if="pagination.totalPages > 1 && !loading" class="pagination-bar">
      <span class="pagination-meta">{{ paginationInfo }}</span>
      <nav aria-label="Paginación">
        <ul class="pagination pagination-sm mb-0">
          <li class="page-item" :class="{ disabled: pagination.currentPage === 1 }">
            <button class="page-link" @click="goToPage(pagination.currentPage - 1)">
              <i class="fas fa-chevron-left"></i>
            </button>
          </li>
          <li
            v-for="page in visiblePages"
            :key="page"
            class="page-item"
            :class="{ active: pagination.currentPage === page, disabled: page === '...' }"
          >
            <button class="page-link" @click="goToPage(page)">{{ page }}</button>
          </li>
          <li class="page-item" :class="{ disabled: pagination.currentPage === pagination.totalPages }">
            <button class="page-link" @click="goToPage(pagination.currentPage + 1)">
              <i class="fas fa-chevron-right"></i>
            </button>
          </li>
        </ul>
      </nav>
    </div>

    <!-- Modal eliminar -->
    <div v-if="showDeleteModal" class="panel-modal-overlay" @click.self="showDeleteModal = false">
      <div class="panel-modal">
        <h3><i class="fas fa-trash-alt me-2 text-danger"></i>Eliminar usuario</h3>
        <template v-if="userToDelete">
          <p class="text-muted">Esta acción no se puede deshacer.</p>
          <div class="delete-preview">
            <span class="avatar">{{ userInitials(userToDelete) }}</span>
            <div>
              <strong>{{ displayName(userToDelete) }}</strong>
              <div class="text-muted small">@{{ userToDelete.username }} · {{ userToDelete.email }}</div>
            </div>
          </div>
        </template>
        <div class="panel-modal-actions">
          <button type="button" class="btn btn-secondary" @click="showDeleteModal = false">Cancelar</button>
          <button type="button" class="btn btn-danger" :disabled="isDeleting" @click="deleteUser">
            <span v-if="isDeleting"><i class="fas fa-spinner fa-spin me-1"></i>Eliminando…</span>
            <span v-else>Eliminar</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal crear -->
    <div v-if="showCreateModal" class="panel-modal-overlay" @click.self="showCreateModal = false">
      <div class="panel-modal panel-modal-lg">
        <h3><i class="fas fa-user-plus me-2"></i>Nuevo usuario</h3>
        <form @submit.prevent="createUser">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">Nombre *</label>
              <input v-model="newUser.first_name" type="text" class="form-control" :class="{ 'is-invalid': createUserErrors.first_name }" required />
              <div v-if="createUserErrors.first_name" class="invalid-feedback d-block">{{ createUserErrors.first_name }}</div>
            </div>
            <div class="col-md-6">
              <label class="form-label">Apellido *</label>
              <input v-model="newUser.last_name" type="text" class="form-control" :class="{ 'is-invalid': createUserErrors.last_name }" required />
              <div v-if="createUserErrors.last_name" class="invalid-feedback d-block">{{ createUserErrors.last_name }}</div>
            </div>
            <div class="col-md-6">
              <label class="form-label">Usuario *</label>
              <input v-model="newUser.username" type="text" class="form-control" :class="{ 'is-invalid': createUserErrors.username }" required />
              <div v-if="createUserErrors.username" class="invalid-feedback d-block">{{ createUserErrors.username }}</div>
            </div>
            <div class="col-md-6">
              <label class="form-label">Email *</label>
              <input v-model="newUser.email" type="email" class="form-control" :class="{ 'is-invalid': createUserErrors.email }" required />
              <div v-if="createUserErrors.email" class="invalid-feedback d-block">{{ createUserErrors.email }}</div>
            </div>
            <div class="col-md-6">
              <label class="form-label">Contraseña *</label>
              <input v-model="newUser.password" type="password" class="form-control" :class="{ 'is-invalid': createUserErrors.password }" required />
              <div v-if="createUserErrors.password" class="invalid-feedback d-block">{{ createUserErrors.password }}</div>
            </div>
            <div class="col-md-6">
              <label class="form-label">Confirmar contraseña *</label>
              <input v-model="newUser.password2" type="password" class="form-control" :class="{ 'is-invalid': createUserErrors.password2 }" required />
              <div v-if="createUserErrors.password2" class="invalid-feedback d-block">{{ createUserErrors.password2 }}</div>
            </div>
            <div class="col-12">
              <label class="form-label">Instagram <span class="text-muted fw-normal">(opcional)</span></label>
              <input v-model="newUser.instagram" type="text" class="form-control" placeholder="@usuario" />
            </div>
            <div v-if="createUserError" class="col-12">
              <div class="alert alert-danger mb-0 py-2">
                <template v-if="Array.isArray(createUserError)">
                  <div v-for="err in createUserError" :key="err">{{ err }}</div>
                </template>
                <template v-else>{{ createUserError }}</template>
              </div>
            </div>
          </div>
          <div class="panel-modal-actions">
            <button type="button" class="btn btn-secondary" @click="showCreateModal = false">Cancelar</button>
            <button type="submit" class="btn btn-primary" :disabled="isCreatingUser">
              <span v-if="isCreatingUser"><i class="fas fa-spinner fa-spin me-1"></i>Creando…</span>
              <span v-else>Crear usuario</span>
            </button>
          </div>
        </form>
      </div>
    </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useToast } from 'vue-toastification'
import { getUsers, deleteUser as deleteUserService, toggleUserStatus as toggleUserStatusService, createUserAsAdmin } from '@/services/auth'
import { getCustomerRoles } from '@/services/invitations'
import apiClient from '@/services/apiClient'
import AccessRequestsManager from '@/pages/admin/AccessRequestsManager.vue'

const toast = useToast()
const users = ref([])
const loading = ref(true)
const error = ref(null)
const roleOptions = ref([])
const pendingCount = ref(0)
const activeQuickFilter = ref('all')

const filters = ref({
  username: '',
  email: '',
  status: '',
  userType: '',
  approval: '',
  roleFilter: '',
  page: 1,
  page_size: 15,
})

const pagination = ref({
  count: 0,
  currentPage: 1,
  totalPages: 1,
})

const userToDelete = ref(null)
const showDeleteModal = ref(false)
const showCreateModal = ref(false)
const isDeleting = ref(false)
const changingRoleId = ref(null)
const currentUserId = ref(parseInt(localStorage.getItem('userId'), 10) || 0)

const viewMode = ref('users')
const requestsPendingCount = ref(0)

const newUser = ref({
  first_name: '',
  last_name: '',
  username: '',
  email: '',
  password: '',
  password2: '',
  instagram: '',
})
const isCreatingUser = ref(false)
const createUserError = ref(null)
const createUserErrors = ref({})

const quickFilters = [
  { key: 'all', label: 'Todos', icon: 'fas fa-users' },
  { key: 'pending', label: 'Email pendiente', icon: 'fas fa-envelope' },
  { key: 'active', label: 'Con compras', icon: 'fas fa-shopping-bag' },
  { key: 'inactive', label: 'Sin compras', icon: 'fas fa-moon' },
]

const paginationInfo = computed(() => {
  if (!pagination.value.count) return '0 usuarios'
  const start = (pagination.value.currentPage - 1) * filters.value.page_size + 1
  const end = Math.min(start + users.value.length - 1, pagination.value.count)
  return `${start}–${end} de ${pagination.value.count}`
})

const hasActiveFilters = computed(() =>
  !!(filters.value.username || filters.value.status || filters.value.approval || filters.value.roleFilter)
)

const visiblePages = computed(() => {
  const total = pagination.value.totalPages
  const current = pagination.value.currentPage
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  if (current <= 3) return [1, 2, 3, 4, 5, '...', total]
  if (current >= total - 2) return [1, '...', total - 4, total - 3, total - 2, total - 1, total]
  return [1, '...', current - 1, current, current + 1, '...', total]
})

let searchTimer = null
const debouncedSearch = () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    filters.value.page = 1
    searchUsers()
  }, 350)
}

onUnmounted(() => clearTimeout(searchTimer))

const userInitials = (user) => {
  const first = (user.first_name || '').trim()
  const last = (user.last_name || '').trim()
  if (first || last) return ((first[0] || '') + (last[0] || '')).toUpperCase()
  return (user.username || '?').slice(0, 2).toUpperCase()
}

const displayName = (user) => {
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim()
  return name || user.username
}

// Estado de la cuenta para la columna B2B: indica si puede (o no) ingresar.
const accountState = (user) => {
  if (!user.is_approved) {
    return {
      text: 'Pendiente',
      cls: 'b2b-pending',
      icon: 'fas fa-clock',
      tooltip: 'Acceso pendiente de aprobación',
    }
  }
  if (user.is_active) {
    return {
      text: 'Habilitada',
      cls: 'b2b-ok',
      icon: 'fas fa-check',
      tooltip: 'Cuenta habilitada: puede iniciar sesión',
    }
  }
  return {
    text: 'No habilitada',
    cls: 'b2b-danger',
    icon: 'fas fa-ban',
    tooltip: 'Cuenta aprobada pero no habilitada para ingresar (email sin activar o cuenta deshabilitada)',
  }
}

const applyQuickFilter = (key) => {
  activeQuickFilter.value = key
  filters.value.status = ''
  filters.value.approval = ''
  if (key === 'pending') filters.value.status = 'pending_email'
  else if (key === 'active') filters.value.status = 'active'
  else if (key === 'inactive') filters.value.status = 'inactive'
  filters.value.page = 1
  searchUsers()
}

const applyFilters = () => {
  filters.value.page = 1
  if (filters.value.status === 'pending_email') activeQuickFilter.value = 'pending'
  else if (filters.value.status === 'active') activeQuickFilter.value = 'active'
  else if (filters.value.status === 'inactive') activeQuickFilter.value = 'inactive'
  else if (!filters.value.username && !filters.value.roleFilter) activeQuickFilter.value = 'all'
  searchUsers()
}

const buildQueryParams = () => {
  const q = { page: filters.value.page, page_size: filters.value.page_size }
  const term = filters.value.username.trim()
  if (term) {
    if (term.includes('@')) {
      q.email = term
    } else {
      q.username = term
    }
  }
  if (filters.value.status) q.status = filters.value.status
  if (filters.value.roleFilter) q.user_type = filters.value.roleFilter
  return q
}

const fetchPendingCount = async () => {
  try {
    const res = await apiClient.get('/auth/users/?status=pending_email&page_size=1')
    pendingCount.value = res.data?.count || 0
  } catch {
    pendingCount.value = 0
  }
}

const searchUsers = async () => {
  loading.value = true
  error.value = null
  try {
    const response = await getUsers(buildQueryParams())
    users.value = response.results || []
    pagination.value = {
      count: response.count || 0,
      currentPage: filters.value.page,
      totalPages: Math.max(1, Math.ceil((response.count || 0) / filters.value.page_size)),
    }
  } catch {
    error.value = 'No se pudieron cargar los usuarios. Intentá de nuevo.'
    toast.error(error.value)
  } finally {
    loading.value = false
  }
}

const refreshUsers = () => {
  filters.value = {
    username: '',
    email: '',
    status: '',
    userType: '',
    approval: '',
    roleFilter: '',
    page: 1,
    page_size: 15,
  }
  activeQuickFilter.value = 'all'
  searchUsers()
}

const goToPage = (page) => {
  if (page === '...' || page < 1 || page > pagination.value.totalPages) return
  filters.value.page = page
  searchUsers()
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const confirmDeleteUser = (user) => {
  userToDelete.value = user
  showDeleteModal.value = true
}

const deleteUser = async () => {
  if (!userToDelete.value) return
  isDeleting.value = true
  try {
    await deleteUserService(userToDelete.value.id)
    toast.success(`Usuario ${userToDelete.value.username} eliminado`)
    showDeleteModal.value = false
    userToDelete.value = null
    await Promise.all([searchUsers(), fetchPendingCount()])
  } catch (err) {
    toast.error(err.response?.data?.error || 'Error al eliminar el usuario')
  } finally {
    isDeleting.value = false
  }
}

const approveUser = async (user) => {
  try {
    await apiClient.patch(`/auth/users/${user.id}/`, { is_approved: true })
    user.is_approved = true
    toast.success(`${user.username} aprobado para B2B`)
    fetchPendingCount()
  } catch (e) {
    toast.error(e.response?.data?.detail || 'Error al aprobar')
  }
}

const changeUserRole = async (user, newRole) => {
  if (!newRole || newRole === user.role) return
  changingRoleId.value = user.id
  const prevRole = user.role
  const prevDisplay = user.role_display
  const prevColor = user.role_color
  try {
    const res = await apiClient.patch(`/auth/users/${user.id}/`, { role: newRole })
    user.role = res.data.role || newRole
    user.role_display = res.data.role_display || newRole
    user.role_color = res.data.role_color || '#adb5bd'
    toast.success(`Rol de ${user.username} cambiado a ${user.role_display}`)
  } catch (e) {
    user.role = prevRole
    user.role_display = prevDisplay
    user.role_color = prevColor
    toast.error(e.response?.data?.detail || e.response?.data?.role?.[0] || 'Error al cambiar el rol')
  } finally {
    changingRoleId.value = null
  }
}

const revokeUser = async (user) => {
  try {
    await apiClient.patch(`/auth/users/${user.id}/`, { is_approved: false })
    user.is_approved = false
    toast.warning(`Acceso B2B revocado para ${user.username}`)
    fetchPendingCount()
  } catch (e) {
    toast.error(e.response?.data?.detail || 'Error al revocar')
  }
}

const toggleUserStatus = async (user, newStatus) => {
  if (user.id === currentUserId.value) {
    toast.warning('No podés modificar tu propia cuenta')
    return
  }
  try {
    await toggleUserStatusService(user.id, newStatus)
    user.is_active = newStatus
    toast.success(`Cuenta ${newStatus ? 'habilitada' : 'deshabilitada'}`)
  } catch (err) {
    toast.error(err.response?.data?.error || 'Error al cambiar el estado de la cuenta')
  }
}

const openCreateUserModal = () => {
  newUser.value = { first_name: '', last_name: '', username: '', email: '', password: '', password2: '', instagram: '' }
  createUserError.value = null
  createUserErrors.value = {}
  showCreateModal.value = true
}

const createUser = async () => {
  isCreatingUser.value = true
  createUserError.value = null
  createUserErrors.value = {}
  try {
    if (newUser.value.password !== newUser.value.password2) {
      createUserErrors.value.password2 = 'Las contraseñas no coinciden'
      return
    }
    const userData = {
      first_name: newUser.value.first_name.trim(),
      last_name: newUser.value.last_name.trim(),
      username: newUser.value.username.trim(),
      email: newUser.value.email.trim().toLowerCase(),
      password: newUser.value.password,
      password2: newUser.value.password2,
      instagram: newUser.value.instagram.trim() || null,
    }
    await createUserAsAdmin(userData)
    toast.success(`Usuario ${userData.username} creado`)
    showCreateModal.value = false
    await Promise.all([searchUsers(), fetchPendingCount()])
  } catch (err) {
    if (err.errors && Array.isArray(err.errors)) createUserError.value = err.errors
    else if (err.response?.data?.error) createUserError.value = err.response.data.error
    else if (err.message) createUserError.value = err.message
    else createUserError.value = 'Error al crear el usuario'
  } finally {
    isCreatingUser.value = false
  }
}

const loadRoles = async () => {
  try {
    const data = await getCustomerRoles({ active_only: true })
    roleOptions.value = Array.isArray(data) ? data : (data.results || [])
  } catch {
    roleOptions.value = []
  }
}

const fetchRequestsPendingCount = async () => {
  try {
    const res = await apiClient.get('/auth/access-requests/?status=pending&page_size=1')
    requestsPendingCount.value = res.data?.count || 0
  } catch {
    requestsPendingCount.value = 0
  }
}

const switchTo = (mode) => {
  viewMode.value = mode
  if (mode === 'requests') fetchRequestsPendingCount()
}

onMounted(async () => {
  await Promise.all([loadRoles(), fetchPendingCount(), searchUsers(), fetchRequestsPendingCount()])
})
</script>

<style scoped>
.user-list-page {
  padding-bottom: 2rem;
}

.view-toggle {
  display: inline-flex;
  gap: 0.5rem;
  padding: 0.35rem;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid var(--panel-border);
  border-radius: 12px;
}

.view-toggle__btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.view-toggle__btn:hover {
  color: var(--white-color);
}

.view-toggle__btn.active {
  background: var(--primary-color);
  color: var(--black-color);
}

.view-toggle__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 0.35rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  background: rgba(0, 0, 0, 0.25);
  color: inherit;
}

.view-toggle__btn.active .view-toggle__badge {
  background: rgba(0, 0, 0, 0.2);
}

.toolbar-left {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
}

.pending-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
  border: 1px solid rgba(245, 158, 11, 0.25);
}

/* Filtros */
.filters-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 1.25rem;
}

.search-wrap {
  position: relative;
  flex: 1;
  min-width: 220px;
}

.search-icon {
  position: absolute;
  left: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.4);
  pointer-events: none;
  font-size: 0.85rem;
}

.search-input {
  padding-left: 2.35rem;
  padding-right: 2.25rem;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid var(--panel-border);
  border-radius: 12px;
  color: var(--white-color);
  min-height: 42px;
}

.search-input:focus {
  background: rgba(0, 0, 0, 0.28);
  border-color: var(--primary-color);
  color: var(--white-color);
  box-shadow: 0 0 0 2px rgba(255, 236, 0, 0.12);
}

.clear-search {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.45);
  cursor: pointer;
  padding: 0.25rem 0.4rem;
  border-radius: 6px;
}

.clear-search:hover {
  color: var(--white-color);
  background: var(--black-color-2);
}

.quick-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.filter-chip {
  border: 1px solid var(--panel-border);
  background: rgba(0, 0, 0, 0.15);
  color: rgba(255, 255, 255, 0.65);
  border-radius: 999px;
  padding: 0.35rem 0.85rem;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.filter-chip:hover {
  color: var(--white-color);
  border-color: rgba(255, 255, 255, 0.2);
}

.filter-chip.active {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: var(--black-color);
}

.role-select {
  max-width: 180px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid var(--panel-border);
  border-radius: 12px;
  color: var(--white-color);
  min-height: 42px;
  font-size: 0.85rem;
}

.role-select:focus {
  background: rgba(0, 0, 0, 0.28);
  border-color: var(--primary-color);
  color: var(--white-color);
  box-shadow: 0 0 0 2px rgba(255, 236, 0, 0.12);
}

/* Tabla */
.user-table thead th {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.45);
  border-bottom-color: var(--black-color-2);
  padding: 0.85rem 1rem;
  white-space: nowrap;
}

.user-table tbody td {
  padding: 0.75rem 1rem;
  border-bottom-color: rgba(255, 255, 255, 0.04);
  vertical-align: middle;
}

.user-row:hover {
  background: rgba(255, 255, 255, 0.02);
}

.user-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.avatar {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: var(--primary-color);
  color: var(--black-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.8rem;
}

.avatar.inactive {
  background: var(--black-color-2);
  color: rgba(255, 255, 255, 0.4);
}

.user-cell-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.user-cell-name {
  font-weight: 600;
  color: var(--white-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-cell-sub {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.45);
}

.user-cell-email {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.35);
  margin-top: 0.1rem;
}

.email-cell {
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.65);
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.role-tag-admin {
  background: rgba(255, 236, 0, 0.2) !important;
  color: var(--primary-color) !important;
}

.role-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.role-inline-select {
  appearance: none;
  background: transparent;
  border: 1px solid;
  border-radius: 999px;
  padding: 0.2rem 1.5rem 0.2rem 0.6rem;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23aaa'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.45rem center;
  transition: opacity 0.15s;
}

.role-inline-select:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.role-inline-select option {
  background: #1a1a1a;
  color: #fff;
}

.role-spin {
  font-size: 0.75rem;
  color: rgba(255,255,255,0.5);
}

.status-badge,
.b2b-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.78rem;
  font-weight: 600;
  border-radius: 999px;
  padding: 0.2rem 0.55rem;
  position: relative;
  cursor: default;
  white-space: nowrap;
}

.status-badge[data-tooltip]:hover::after,
.b2b-badge[data-tooltip]:hover::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 180px;
  max-width: 260px;
  padding: 0.45rem 0.65rem;
  border-radius: 8px;
  background: #111;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 500;
  line-height: 1.35;
  text-align: center;
  white-space: normal;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  z-index: 20;
  pointer-events: none;
}

.status-badge[data-tooltip]:hover::before,
.b2b-badge[data-tooltip]:hover::before {
  content: '';
  position: absolute;
  bottom: calc(100% + 2px);
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: #111;
  z-index: 21;
  pointer-events: none;
}

.status-ok {
  background: rgba(16, 185, 129, 0.12);
  color: #10b981;
}

.status-warn {
  background: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
}

.status-muted {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.45);
}

.b2b-ok {
  background: rgba(16, 185, 129, 0.12);
  color: #10b981;
}

.b2b-danger {
  background: rgba(239, 68, 68, 0.14);
  color: #f87171;
}

.b2b-pending {
  background: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
}

.row-actions {
  display: inline-flex;
  gap: 0.25rem;
  justify-content: flex-end;
}

.btn-icon {
  width: 32px;
  height: 32px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: var(--black-color-2);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.7);
  transition: background 0.15s, color 0.15s;
}

.btn-icon:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  color: var(--white-color);
}

.btn-icon-success:hover:not(:disabled) { color: #28a745; border-color: rgba(40, 167, 69, 0.3); }
.btn-icon-warning:hover:not(:disabled) { color: #f59e0b; border-color: rgba(245, 158, 11, 0.3); }
.btn-icon-danger:hover:not(:disabled) { color: #dc3545; border-color: rgba(220, 53, 69, 0.3); }

/* Skeleton */
.table-skeleton {
  padding: 0.5rem 1rem;
}

.skeleton-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.skeleton-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(90deg, var(--black-color-2) 25%, rgba(255,255,255,0.06) 50%, var(--black-color-2) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

.skeleton-lines { flex: 1; }

.skeleton-line {
  height: 10px;
  border-radius: 4px;
  margin-bottom: 0.4rem;
  background: linear-gradient(90deg, var(--black-color-2) 25%, rgba(255,255,255,0.06) 50%, var(--black-color-2) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

.skeleton-line.w-40 { width: 40%; }
.skeleton-line.w-60 { width: 60%; }

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Estados vacío / error */
.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: var(--panel-surface);
  border: 1px solid var(--panel-border);
  border-radius: 16px;
  color: var(--white-color);
}

.state-box h3 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
}

.state-error {
  border-color: rgba(220, 53, 69, 0.3);
}

/* Paginación */
.pagination-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.25rem;
}

.pagination-meta {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
}

.page-link {
  background: rgba(0, 0, 0, 0.2);
  border-color: var(--panel-border);
  color: rgba(255, 255, 255, 0.7);
  min-width: 34px;
  text-align: center;
}

.page-item.active .page-link {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: var(--black-color);
}

.page-link:hover:not(:disabled) {
  background: var(--black-color-2);
  color: var(--white-color);
}

/* Modal eliminar */
.delete-preview {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1rem;
  background: var(--black-color-2);
  border-radius: 12px;
  margin: 1rem 0;
}

.user-list-page .form-control,
.user-list-page .form-select {
  background: var(--black-color-2);
  border-color: rgba(255, 255, 255, 0.12);
  color: var(--white-color);
}

.user-list-page .form-control:focus,
.user-list-page .form-select:focus {
  background: var(--black-color-2);
  border-color: var(--primary-color);
  color: var(--white-color);
  box-shadow: 0 0 0 2px rgba(255, 236, 0, 0.12);
}

.user-list-page .form-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 0.35rem;
}

@media (max-width: 767.98px) {
  .filters-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .role-select {
    max-width: 100%;
  }

  .quick-filters {
    flex-wrap: nowrap;
    padding-bottom: 0.25rem;
  }

  .filter-chip {
    white-space: nowrap;
    flex-shrink: 0;
  }

  .panel-data-card .user-cell {
    gap: 0.65rem;
  }

  .panel-data-card .role-inline-select {
    max-width: 100%;
    font-size: 0.8rem;
  }
}
</style>
